import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Send, X, Loader2 } from 'lucide-react';
import { UserProfile, ROLE_LABELS } from '@/types/user';
import { formatDistanceToNow } from 'date-fns';
import { RoleBadge } from './RoleBadge';

interface InvitedUsersSectionProps {
  users: UserProfile[];
  onResendInvite: (userId: string) => Promise<void>;
  onCancelInvite: (userId: string) => Promise<void>;
}

export function InvitedUsersSection({ users, onResendInvite, onCancelInvite }: InvitedUsersSectionProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, 'resend' | 'cancel' | null>>({});

  if (users.length === 0) return null;

  const handleResend = async (userId: string) => {
    setLoadingStates(prev => ({ ...prev, [userId]: 'resend' }));
    try {
      await onResendInvite(userId);
    } finally {
      setLoadingStates(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleCancel = async (userId: string) => {
    setLoadingStates(prev => ({ ...prev, [userId]: 'cancel' }));
    try {
      await onCancelInvite(userId);
    } finally {
      setLoadingStates(prev => ({ ...prev, [userId]: null }));
    }
  };

  return (
    <Card className="border-info/30 bg-info/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-info">
          <Clock className="h-5 w-5" />
          Invited - Awaiting Signup ({users.length} {users.length === 1 ? 'user' : 'users'})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {users.map((user) => {
            const loading = loadingStates[user.id];
            return (
              <div
                key={user.id}
                className="flex items-center justify-between p-3 bg-card rounded-lg border"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{user.email}</span>
                    <RoleBadge role={user.role} />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Invited {user.invited_at ? formatDistanceToNow(new Date(user.invited_at), { addSuffix: true }) : 'recently'}
                    {user.invited_by_profile && ` by ${user.invited_by_profile.display_name || user.invited_by_profile.email}`}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResend(user.id)}
                    disabled={!!loading}
                  >
                    {loading === 'resend' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-1" />
                        Resend
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCancel(user.id)}
                    disabled={!!loading}
                    className="text-destructive hover:text-destructive"
                  >
                    {loading === 'cancel' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
